import React, { useState, useEffect } from 'react';
import { Book, Home, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ChristianTip, fetchChristianTips } from '../lib/christian-tips';
import ChristianTipCard from '../components/ChristianTipCard';
import ChristianTipFilter from '../components/ChristianTipFilter';

export default function ChristianTips() {
  const [tips, setTips] = useState<ChristianTip[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedAgeRating, setSelectedAgeRating] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'title'>('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  
  const perPage = 12;

  useEffect(() => {
    loadTips();
  }, [currentPage, selectedType, selectedAgeRating, sortBy]);

  async function loadTips() {
    setLoading(true);
    try {
      const { data, count } = await fetchChristianTips({
        type: selectedType as 'livro' | 'filme' | undefined,
        ageRating: selectedAgeRating,
        page: currentPage,
        perPage,
        orderBy: sortBy === 'recent' ? 'published_at' : 'title',
        orderDirection: sortBy === 'recent' ? 'desc' : 'asc'
      });
      
      setTips(data);
      setTotalCount(count);
    } catch (error) {
      console.error('Error loading tips:', error);
    } finally {
      setLoading(false);
    }
  }

  function resetFilters() {
    setSelectedType(null);
    setSelectedAgeRating(null);
    setSortBy('recent');
    setCurrentPage(1);
  }

  // Filtra os resultados localmente com base na busca
  const filteredTips = tips.filter(tip => 
    searchQuery ? 
      tip.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      tip.description.toLowerCase().includes(searchQuery.toLowerCase())
    : true
  );

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Book className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Dicas Cristãs</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      <div className="mb-6">
        <p className="text-gray-600">
          Confira nossas recomendações de livros e filmes cristãos para edificação espiritual e entretenimento saudável.
        </p>
      </div>

      {/* Barra de busca */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por título ou descrição..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
        />
      </div>

      {/* Filtros */}
      <ChristianTipFilter
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedAgeRating={selectedAgeRating}
        setSelectedAgeRating={setSelectedAgeRating}
        sortBy={sortBy}
        setSortBy={setSortBy}
        resetFilters={resetFilters}
      />

      {/* Lista de dicas */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      ) : filteredTips.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredTips.map((tip) => (
              <ChristianTipCard key={tip.id} tip={tip} />
            ))}
          </div>
          
          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                >
                  Anterior
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-full ${
                      currentPage === page
                        ? 'bg-brand-teal text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Book className="h-16 w-16 text-brand-teal/20 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-brand-teal mb-2">
            Nenhuma recomendação encontrada
          </h2>
          <p className="text-gray-600 max-w-md mx-auto">
            {searchQuery
              ? 'Não encontramos resultados para sua busca. Tente outros termos ou remova os filtros.'
              : 'Não há recomendações disponíveis no momento. Volte em breve para novidades!'}
          </p>
          {(searchQuery || selectedType || selectedAgeRating) && (
            <button
              onClick={resetFilters}
              className="mt-4 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
}